import { useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '@/core/auth/AuthProvider';
import { useActorRole, useCan } from '@/core/auth/permissions';
import { Badge, Container, cx } from '@/design-system';

/**
 * CASCA DO PAINEL
 *
 * O menu é montado a partir das permissões efetivas: o financeiro não vê
 * "Moderação", o moderador não vê "Financeiro".
 *
 * Isso é clareza, não segurança. Digitar `/admin/financeiro` na barra de
 * endereço leva à guarda de rota, e a guarda leva ao servidor — que é quem
 * recusa. Esconder o item de menu só evita oferecer um caminho fechado.
 */

type AdminNavItem = {
  to: string;
  label: string;
  permission: string | string[];
  end?: boolean;

  /**
   * Área prevista no documento funcional mas ainda sem tela.
   *
   * Aparece no menu, desabilitada e marcada — em vez de virar link morto
   * para o 404, que foi o que aconteceu antes.
   *
   * NENHUM ITEM USA ISTO HOJE: CRM ganhou tela e Financeiro saiu do menu. O
   * mecanismo fica porque é o jeito certo de anunciar uma área que está
   * chegando — mas usá-lo tem prazo. Item "em breve" que passa meses no menu
   * cansa antes de informar, e foi por isso que os dois saíram.
   */
  pending?: string;

  /**
   * Perfil exigido, além da permissão.
   *
   * Existe por causa de uma tela só: Chaves de pagamento. Ela é a única do
   * painel com dois portões — `payments.view` na rota e `admin_master` por
   * dentro — porque revelar credencial de cobrança não é a mesma decisão que
   * ver uma cobrança.
   *
   * Sem isto, o menu mostraria o link para o Financeiro (que tem
   * `payments.view`) e a tela o recusaria na entrada. Anunciar um caminho
   * fechado é o defeito que o comentário do "em breve" acima já descreve.
   */
  role?: string;

};
type AdminNavGroup = {
  title: string;
  items: AdminNavItem[];
};

const NAV: AdminNavGroup[] = [
  {
    title: 'Operação',
    items: [
      { to: '/admin', label: 'Visão geral', permission: ['listings.view', 'payments.view', 'leads.view'], end: true },
      { to: '/admin/imoveis', label: 'Imóveis', permission: 'listings.view' },
      { to: '/admin/moderacao', label: 'Moderação', permission: ['listings.approve', 'reports.view'] },

      // Separado de Moderação de propósito: lá se decide sobre o anúncio, aqui
      // se tria o que o público relatou. Quem tem `reports.view` sem
      // `listings.approve` precisa chegar nesta e não naquela.
      { to: '/admin/denuncias', label: 'Denúncias', permission: 'reports.view' },
      { to: '/admin/crm', label: 'CRM e contatos', permission: 'leads.view' },
    ],
  },
  {
    title: 'Comercial',
    items: [
      /*
        "FINANCEIRO" SAIU DO MENU.

        Era o último item marcado "em breve" desta lista — visível, cinza, sem
        tela por trás. Anunciar uma área que não existe cansa antes de
        informar: quem opera o painel passa meses lendo a mesma promessa.

        Quem precisa agir sobre dinheiro hoje vai por Assinantes, que lista os
        inadimplentes primeiro e existe de verdade. O perfil Financeiro
        continua, com as permissões que sempre teve — o que saiu foi a linha do
        menu, não o acesso.
      */
      { to: '/admin/assinantes', label: 'Assinantes', permission: 'subscriptions.view' },
      { to: '/admin/planos', label: 'Planos', permission: 'plans.view' },
    ],
  },
  {
    title: 'Configuração',
    items: [
      { to: '/admin/site', label: 'Identidade do site', permission: 'settings.edit' },
      { to: '/admin/conteudo', label: 'Páginas e textos', permission: 'content.edit' },
      { to: '/admin/formularios', label: 'Formulários', permission: 'forms.edit' },
    ],
  },
  {
    title: 'Acesso e segurança',
    items: [
      { to: '/admin/usuarios', label: 'Usuários internos', permission: 'users.view' },
      { to: '/admin/perfis', label: 'Perfis e permissões', permission: 'roles.view' },
      { to: '/admin/auditoria', label: 'Auditoria', permission: 'audit.view' },

      // Fica aqui, e nao em "Configuracao": o perfil Administrador e descrito
      // como "opera o negocio inteiro, MENOS perfis e integracoes" — e este e
      // o item de integracao. Vizinho de Perfis, que tem o mesmo dono.
      { to: '/admin/chaves-de-pagamento', label: 'Chaves de pagamento', permission: 'payments.view', role: 'admin_master' },
    ],
  },
];

function NavGroup({ group, onNavigate }: { group: AdminNavGroup; onNavigate?: () => void }) {
  const can = useCan();
  const { user } = useAuth();

  // O item so aparece quando as DUAS condicoes valem. Ver "role" no tipo
  // acima: mostrar link que a tela recusa e pior que nao mostrar link.
  const visible = group.items.filter(
    (item) => can(item.permission) && (!item.role || (user?.roles ?? []).includes(item.role)),
  );

  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <span className="px-3 text-[0.6875rem] font-bold uppercase tracking-[0.12em] text-ink-faint">{group.title}</span>

      {visible.map((item) =>
        item.pending ? (
          <span
            key={item.to}
            aria-disabled="true"
            title={`Previsto no documento funcional (${item.pending}), ainda não implementado.`}
            className="flex cursor-not-allowed items-center justify-between gap-2 rounded-(--radius-control) px-3 py-2 text-[0.9375rem] text-ink-faint"
          >
            {item.label}
            <span className="rounded-full bg-surface-muted px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wide">
              em breve
            </span>
          </span>
        ) : (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              cx(
                'rounded-(--radius-control) px-3 py-2 text-[0.9375rem] no-underline transition-colors',
                isActive ? 'bg-primary-soft font-semibold text-primary' : 'text-ink-soft hover:bg-surface-muted hover:text-ink',
              )
            }
          >
            {item.label}
          </NavLink>
        ),
      )}
    </div>
  );
}

export function AdminLayout() {
  const { user } = useAuth();
  const { role } = useActorRole();
  const [menuOpen, setMenuOpen] = useState(false);

  const sidebar = (
    <nav className="flex flex-col gap-6" aria-label="Menu do painel">
      {NAV.map((group) => (
        <NavGroup key={group.title} group={group} onNavigate={() => setMenuOpen(false)} />
      ))}
    </nav>
  );

  return (
    <Container>
      <div className="flex flex-col gap-6 py-8">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line pb-5">
          <div className="flex flex-col gap-1">
            <h1 className="font-display text-2xl font-extrabold text-ink">Painel administrativo</h1>
            <p className="text-sm text-ink-soft">
              {user?.name}
              {role ? (
                <>
                  {' · '}
                  <Badge tone={role.level >= 50 ? 'primary' : 'neutral'}>{role.name}</Badge>
                </>
              ) : null}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="rounded-(--radius-control) border border-line px-3 py-2 text-sm font-semibold text-ink lg:hidden"
            aria-expanded={menuOpen}
          >
            {menuOpen ? 'Fechar menu' : 'Menu do painel'}
          </button>
        </header>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className={cx(menuOpen ? 'block' : 'hidden lg:block', 'lg:sticky lg:top-24 lg:self-start')}>
            {sidebar}
          </aside>

          <div className="min-w-0">
            <Outlet />
          </div>
        </div>
      </div>
    </Container>
  );
}

/** Cabeçalho padrão das telas internas do painel. */
export function AdminPage({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex max-w-2xl flex-col gap-1">
          <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
          {description ? <p className="text-sm text-ink-soft">{description}</p> : null}
        </div>
        {actions}
      </div>

      {children}
    </div>
  );
}
